import { bindable, autoinject } from 'aurelia-framework';
import { WebApiService } from '../../../services/web-api-service';
import { BusyService } from '../../../services/busy-service';
import { OnboardingRequirementDto, OnboardingRequirementTypes } from '../../../models/onboarding-requirement';
import 'jquery';
import { SecurityService } from '../../../services/security-service';
import { CarrierRequirementDialog } from '../../dialogs/carrier-requirement-dialog/carrier-requirement-dialog';
import { DialogService } from 'aurelia-dialog';
import { bind } from 'bluebird';


@autoinject()
export class OnboardingRequirements {

    @bindable()
    public showHeader = false;

    public onboardingRequirementTypes = OnboardingRequirementTypes;

    @bindable()
    public showOptionalRequirements = false;

    @bindable openInsurance: Function;
    @bindable refreshInsurance: Function;

    constructor(
        private dialogService: DialogService,
        private api: WebApiService,
        private busyService: BusyService,
        private securityService: SecurityService
    ) {
    }

    public userView: "CarrierService" | "Contracts & Insurance" | "ReadOnly" = undefined;

    @bindable()
    public readonly: boolean = true;

    /** gets/sets whether a carrier services agent can clear rejection
     * Should only be true when displayed when the request is in the onboarding stage */
    @bindable()
    public carrierServicesCanClearRejection: boolean = false;

    @bindable()
    public carrierId: string = "none";

    @bindable()
    public carrierIntId: number = -1;

    @bindable()
    public carrierName: string = undefined;

    public requirementCompletionPercentage = 0;
    public requirementVerifiedPercentage = 0;

    /* for processors, this is success when all documentation is uploaded
        for C&I/Legal, this is success when all documentation is verified
        for viewers, this is warning when uploaded, but not verified, success once fully verified
    */
    public completionIndicatorClass: "incomplete" | "partial" | "complete" = "incomplete";

    public onboardingRequirements: OnboardingRequirementDto[] = [];

    @bindable()
    public options = { statusIcon: "fa-question" };

    async attached() {
        await this.loadDataAsync();
    }

    public async refresh() {
        await this.loadDataAsync();
        this.refreshInsurance();
    }

    // update/merge the onboardingRequirements array so that it matches the reqs array.
    private updateOnboardingRequirements(reqs: OnboardingRequirementDto[]): void {

        // remove any that don't exist from server
        for (let i = this.onboardingRequirements.length - 1; i >= 0; i--) {
            if (!reqs.some(r => r.name === this.onboardingRequirements[i].name)) {
                this.onboardingRequirements.splice(i, 1);
            }
        }


        // add/insert new ones
        reqs.forEach(req => {
            const existingReq = this.onboardingRequirements.find(r => {
                return r.name === req.name;

                // if( r.isCategory){
                //     //match on category name
                //     return r.name === req.name;
                // }else{
                //     //match on carrier requirement id
                //     return r.carrierMasterApprovalRequirementId === req.carrierMasterApprovalRequirementId;
                // }
            });

            if (existingReq) {
                // Update existing item in place
                Object.keys(req).forEach(key => {
                    existingReq[key] = req[key];
                });
            } else {
                // Add new item
                this.onboardingRequirements.push(req);
            }
        });
    }

    private getTotalCount() {
        let count = 0;
        this.onboardingRequirements.forEach(o => {
            if (o.isCategory) {
                count += o.requirements.length;
            } else {
                count++;
            }
        })

        return count;
    }

    private getCompletedCount() {
        let count = 0;
        this.onboardingRequirements.forEach(o => {
            if (o.isCategory) {
                count += o.requirements.filter(o => o.isComplete).length;
            } else {
                if (o.isComplete) {
                    count++;
                }
            }
        })

        return count;
    }

    private getVerifiedCount() {
        let count = 0;
        this.onboardingRequirements.forEach(o => {
            if (o.isCategory) {
                count += o.requirements.filter(o => (o.verifiedStatus == 'Y' || o.verifiedStatus == 'N')).length;
            } else {
                if ((o.verifiedStatus == 'Y' || o.verifiedStatus == 'N')) {
                    count++;
                }
            }
        })

        return count;
    }

    async loadDataAsync() {
        try {
            this.busyService.on();
            var params = { carrierId: this.carrierId };
            const result = await this.api.get("Carriers", "GetOnboardingRequirements", params);
            const reqs: OnboardingRequirementDto[] = JSON.parse(result.response) || [];

            // update/merge the onboardingRequirements array so that it matches the reqs array.
            this.updateOnboardingRequirements(reqs);

            // calculation completion percentage
            const numberOfReqs = this.getTotalCount();
            const numberOfCompletedReqs = this.getCompletedCount();
            this.requirementCompletionPercentage = Math.round(numberOfCompletedReqs / numberOfReqs * 100);

            const numberOfVerifiedReqs = this.getVerifiedCount();
            this.requirementVerifiedPercentage = Math.round(numberOfVerifiedReqs / numberOfReqs * 100);

            //show competion indicator color based on persona
            if (this.securityService.hasRole("Legal")) {
                this.userView = "Contracts & Insurance";
                this.completionIndicatorClass = this.requirementVerifiedPercentage == 100 ? 'complete' : 'incomplete';
            } else if (this.securityService.hasRole("Edit")) {
                this.userView = "CarrierService";
                this.completionIndicatorClass = this.requirementCompletionPercentage == 100 ? 'complete' : 'incomplete';
            } else {
                this.userView = "ReadOnly";
                if (this.requirementVerifiedPercentage == 100) {
                    this.completionIndicatorClass = "complete";
                } else if (this.requirementCompletionPercentage == 100) {
                    this.completionIndicatorClass = "partial";
                } else {
                    this.completionIndicatorClass = "incomplete";
                }
            }

            // sanity check, once in a while, the data load was happening before the bindings updated
            if (params.carrierId !== this.carrierId) {
                const msg = "Data mismatch detected.  Please report this message to Joseph Gabriel for CMP support";
                alert(msg);
                throw new Error(msg);
            }

        } finally {
            this.busyService.off();
        }
    }


    public addNewRequirement() {
        this.dialogService.open(
            {
                viewModel: CarrierRequirementDialog,
                model: {
                    carrierIntId: this.carrierIntId,
                    carrierId: this.carrierId,
                    carrierName: this.carrierName
                }
            }).whenClosed((result) => {
                if (!result.wasCancelled) {
                    this.refresh();
                }
            });
    }

    detached() {
    }

}